import React, {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Routes as ReactRouterDomRoutes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import Flex from '../../../../components/Flex';
import MailNavigation from '../MailNavigation';
import {
  getActiveMailFolderId,
  getMailSettings,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { MailFolderId, ProjectId } from '../../../../models/Types';
import MailNavigationBar from '../NavigationBar/MailNavigationBar';
import { setMailListNavigationState } from '../../actions/actionControllers/mailNavigationActionController';
import MailRowLayout from '../layouts/MailRowLayout';
import MailColumnLayout from '../layouts/MailColumnLayout';
import MailHiddenLayout from '../layouts/MailHiddenLayout';
import MailSettingsDrawer from '../MailSettingsDrawer';
import { fetchMailFoldersSagaAction } from '../../actions/sagas';
import { addDraftMessage } from '../../actions/actionControllers/draftsActionController';
import { apiCreateMessageDraft } from '../../api';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { makePrioStyles } from '../../../../theme/utils';
import { MailFilterContext } from '../MailSearch/MailFilterContext';
import useSelectedMessages from '../../hooks/useSelectedMessages';
import useSharedMailboxAutoMarkAsRead from '../../hooks/useSharedMailboxAutoMarkAsRead';
import AddressesContextProvider from '../AddressesContextProvider';
import { UltimateFilterSearchRef } from '../../../../components/UltimateFilter/createUltimateFilter';
import { MailSearchFormModel } from '../../../../models/MailSearch';
const Routes = Sentry.withSentryReactRouterV6Routing(ReactRouterDomRoutes);

const useStyles = makePrioStyles((theme) => ({
  root: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    height: '100%',
    overflow: 'hidden',
  },
}));

interface MailProjectSubModuleProps {
  projectId: ProjectId;
}

export const MailProjectSubModule: React.FC<MailProjectSubModuleProps> =
  React.memo((props) => {
    //#region ------------------------------ Defaults
    const classes = useStyles();
    const { projectId } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const mailSettings = useSelector(getMailSettings);

    const [selectedMessages, setSelectedMessages] =
      useSelectedMessages(projectId);

    const {
      isEnabled: isAutomaticReadEnabled,
      isLoading: isAutomaticReadLoading,
    } = useSharedMailboxAutoMarkAsRead(projectId);

    const isAutomaticRead = useCallback(
      () => isAutomaticReadEnabled && !isAutomaticReadLoading,
      [isAutomaticReadEnabled, isAutomaticReadLoading]
    );

    const [mailSettingsDrawerVisible, setMailSettingsDrawerVisible] =
      useState<boolean>(false);

    const activeMailFolderId = useSelector<RootReducerState, MailFolderId>(
      (state) => getActiveMailFolderId(state, projectId)
    );

    const ultimateFilterRef =
      useContext<
        MutableRefObject<UltimateFilterSearchRef<MailSearchFormModel>>
      >(MailFilterContext);
    const location = useLocation();
    const presetFilter = location.state?.presetFilter;

    //#endregion

    //#region ------------------------------ Methods / Handlers
    const clearSelection = () => setSelectedMessages([]);

    const doShowNewMessage = async (
      setIsCreating: (value: boolean) => void
    ) => {
      setIsCreating(true);
      const { data } = await apiCreateMessageDraft(projectId);
      if (data) {
        dispatch(addDraftMessage(projectId, data, true));
      } else {
        notification.open({
          message: t('common:error'),
          description: t('mail:errorMessages.messages.createDraftError'),
        });
      }
      setIsCreating(false);
    };
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      setTimeout(() => {
        dispatch(fetchMailFoldersSagaAction(projectId));
      }, 500);
    }, [projectId, dispatch]);

    /*
    useEffect(() => {
      const handler = setTimeout(() => {
        dispatch(deleteProjectNews(projectId));
      }, 5000);
      return () => {
        clearTimeout(handler);
      };
    }, [projectId, dispatch]);
    */
    useEffect(() => {
      setTimeout(() => {
        if (presetFilter) {
          ultimateFilterRef.current?.setFilterValue(presetFilter, true);
        }
      }, 500);
    }, [presetFilter, ultimateFilterRef]);

    useEffect(() => {
      if (
        projectId &&
        !window.location.href.match(/message\/(.*)\/details?$/)
      ) {
        dispatch(setMailListNavigationState(null, projectId));
      }
    }, [projectId, dispatch]);
    //#endregion

    return (
      <Flex.Column className={classes.root}>
        <AddressesContextProvider projectId={projectId}>
          <MailNavigationBar
            projectId={projectId}
            doShowNewMessage={doShowNewMessage}
            selectedMessages={selectedMessages}
            clearSelection={clearSelection}
          />
          <Flex.Row flex={1} className={classes.content}>
            <Routes>
              <Route
                path="*"
                element={<Navigate to={activeMailFolderId ?? 'inbox'} />}
              />
              <Route
                path=":mailFolderId/*"
                element={
                  <MailNavigation
                    projectId={projectId}
                    handleOpenSettings={setMailSettingsDrawerVisible}
                    openSettings={mailSettingsDrawerVisible}
                    clearSelectedMessages={setSelectedMessages}
                  />
                }
              />
              <Route
                path=":mailFolderId/message/:messageId/details"
                element={
                  <MailNavigation
                    projectId={projectId}
                    handleOpenSettings={setMailSettingsDrawerVisible}
                    openSettings={mailSettingsDrawerVisible}
                    clearSelectedMessages={setSelectedMessages}
                  />
                }
              />
            </Routes>
            {mailSettings.messageViewLayout === 'right' ? (
              <MailRowLayout
                projectId={projectId}
                pathPrefix="../../../"
                selectedMessages={selectedMessages}
                onSelectionChange={setSelectedMessages}
                mailSettings={mailSettings}
                isAutomaticRead={isAutomaticRead}
              />
            ) : mailSettings.messageViewLayout === 'bottom' ? (
              <MailColumnLayout
                projectId={projectId}
                pathPrefix="../../../"
                selectedMessages={selectedMessages}
                onSelectionChange={setSelectedMessages}
                mailSettings={mailSettings}
                isAutomaticRead={isAutomaticRead}
              />
            ) : (
              <MailHiddenLayout
                projectId={projectId}
                pathPrefix="../../../"
                selectedMessages={selectedMessages}
                onSelectionChange={setSelectedMessages}
                mailSettings={mailSettings}
                isAutomaticRead={isAutomaticRead}
              />
            )}
            <MailSettingsDrawer
              mailSettings={mailSettings}
              mailSettingsDrawerVisible={mailSettingsDrawerVisible}
              setMailSettingsDrawerVisible={setMailSettingsDrawerVisible}
            />
          </Flex.Row>
        </AddressesContextProvider>
      </Flex.Column>
    );
  });

export default MailProjectSubModule;
